import { useState, useEffect } from 'react';
import { Button } from '../../ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { Badge } from '../../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import {
  Building,
  Users,
  FileText,
  Star,
  FolderOpen,
  Phone,
  Mail,
  MapPin,
  Globe,
  CreditCard,
  CheckCircle,
  ArrowLeft
} from 'lucide-react';
import { VendorContacts } from './VendorContacts';
import { VendorContracts } from './VendorContracts';
import { VendorEvaluations } from './VendorEvaluations';
import { VendorDocuments } from './VendorDocuments';

interface VendorDetailPageProps {
  vendor: any;
  onBack: () => void;
}

export function VendorDetailPage({ vendor, onBack }: VendorDetailPageProps) {
  const [activeTab, setActiveTab] = useState('overview');

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-green-100 text-green-800 border-green-200"><CheckCircle className="h-3 w-3 mr-1" />Active</Badge>;
      case 'inactive':
        return <Badge variant="outline">Inactive</Badge>;
      case 'pending_approval':
        return <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200">Pending Approval</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header with Back Button */}
      <div className="flex items-center gap-4">
        <Button
          variant="outline"
          size="icon"
          onClick={onBack}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1 flex items-start justify-between">
          <div>
            <h2 className="text-2xl font-semibold flex items-center gap-2">
              <Building className="h-6 w-6" />
              {vendor.name}
            </h2>
            <p className="text-muted-foreground mt-1">
              {vendor.primaryCategory} • {vendor.email}
            </p>
          </div>
          {getStatusBadge(vendor.status)}
        </div>
      </div>

      {/* Tabs Content */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="overview">
            <Building className="h-4 w-4 mr-2" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="contacts">
            <Users className="h-4 w-4 mr-2" />
            Contacts
          </TabsTrigger>
          <TabsTrigger value="contracts">
            <FileText className="h-4 w-4 mr-2" />
            Contracts
          </TabsTrigger>
          <TabsTrigger value="documents">
            <FolderOpen className="h-4 w-4 mr-2" />
            Documents
          </TabsTrigger>
          <TabsTrigger value="evaluations">
            <Star className="h-4 w-4 mr-2" />
            Performance
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4 mt-4">
          <div className="grid grid-cols-2 gap-4">
            {/* Company Information */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Building className="h-4 w-4" />
                  Company Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-start gap-2">
                  <Users className="h-4 w-4 text-muted-foreground mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium">Contact Person</p>
                    <p className="text-sm text-muted-foreground">{vendor.contactPerson || 'N/A'}</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <Mail className="h-4 w-4 text-muted-foreground mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium">Email</p>
                    <p className="text-sm text-muted-foreground">{vendor.email || 'N/A'}</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <Phone className="h-4 w-4 text-muted-foreground mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium">Phone</p>
                    <p className="text-sm text-muted-foreground">{vendor.phone || 'N/A'}</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <Globe className="h-4 w-4 text-muted-foreground mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium">Website</p>
                    <p className="text-sm text-muted-foreground">{vendor.website || 'N/A'}</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium">Address</p>
                    <p className="text-sm text-muted-foreground">{vendor.address || 'N/A'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Business Details */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Business Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <p className="text-sm font-medium">Vendor Type</p>
                  <Badge variant="outline" className="mt-1">{vendor.primaryCategory}</Badge>
                </div>
                <div>
                  <p className="text-sm font-medium">Credit Terms</p>
                  <p className="text-sm text-muted-foreground">{vendor.creditPeriod || 'N/A'} days</p>
                </div>
                <div>
                  <p className="text-sm font-medium">Total Orders</p>
                  <p className="text-sm text-muted-foreground">{vendor.totalOrders || 0} orders</p>
                </div>
                <div>
                  <p className="text-sm font-medium">Total Value</p>
                  <p className="text-sm text-muted-foreground">${(vendor.totalValue || 0).toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-sm font-medium">Last Order</p>
                  <p className="text-sm text-muted-foreground">{vendor.lastOrder || 'N/A'}</p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Performance Summary */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Star className="h-4 w-4" />
                Performance Summary
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <div className="flex items-center">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      className={`h-5 w-5 ${i < Math.floor(vendor.rating || 0) ? 'fill-yellow-500 text-yellow-500' : 'text-gray-300'}`}
                    />
                  ))}
                </div>
                <span className="text-lg font-semibold">{vendor.rating || 0}/5.0</span>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="contacts" className="mt-4">
          <VendorContacts vendorId={vendor.id} vendorName={vendor.name} />
        </TabsContent>

        <TabsContent value="contracts" className="mt-4">
          <VendorContracts vendorId={vendor.id} vendorName={vendor.name} />
        </TabsContent>

        <TabsContent value="documents" className="mt-4">
          <VendorDocuments vendorId={vendor.id} vendorName={vendor.name} />
        </TabsContent>

        <TabsContent value="evaluations" className="mt-4">
          <VendorEvaluations vendorId={vendor.id} vendorName={vendor.name} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
